// Seed units, unit systems and conversion.
// Factors are exact SI values. FOUR VALUES ARE CORRECTED vs. the Swift SeedUnit.swift
// (US fl oz ×10, US tsp ×100, imperial fl oz ×10, imperial tbsp ×100 were wrong there).

export type Dimension = 'mass' | 'volume';
export type UnitSystem = 'metric' | 'usCustomary' | 'britishImperial' | 'grug';

export type SeedUnitKey =
  | 'milligram' | 'gram' | 'kilogram'
  | 'milliliter' | 'centiliter' | 'deciliter' | 'liter' | 'metricTeaspoon' | 'metricTablespoon'
  | 'ounce' | 'pound'
  | 'usFluidOunce' | 'usCup' | 'usPint' | 'usQuart' | 'usGallon' | 'usTeaspoon' | 'usTablespoon'
  | 'imperialFluidOunce' | 'imperialCup' | 'imperialPint' | 'imperialQuart' | 'imperialGallon'
  | 'imperialTeaspoon' | 'imperialTablespoon';

export type SeedUnit = {
  key: SeedUnitKey;
  title: string;
  symbol: string;
  searchTerms: string[];
  dimension: Dimension;
  /** Size in the base unit: kilograms for mass, litres for volume. */
  toBase: number;
};

const u = (
  key: SeedUnitKey, title: string, symbol: string, dimension: Dimension, toBase: number, searchTerms: string[],
): SeedUnit => ({ key, title, symbol, dimension, toBase, searchTerms });

// Order matters: it is Swift's SeedUnit.allCases order (used for term matching ties).
export const SEED_UNITS: SeedUnit[] = [
  u('milligram', 'Milligram', 'mg', 'mass', 1e-6, ['mg', 'milligram', 'milligrams']),
  u('gram', 'Gram', 'g', 'mass', 1e-3, ['g', 'gram', 'grams']),
  u('kilogram', 'Kilogram', 'kg', 'mass', 1, ['kg', 'kilogram', 'kilograms']),
  u('milliliter', 'Milliliter', 'ml', 'volume', 1e-3, ['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres']),
  u('centiliter', 'Centiliter', 'cl', 'volume', 1e-2, ['cl', 'centiliter', 'centiliters', 'centilitre', 'centilitres']),
  u('deciliter', 'Deciliter', 'dl', 'volume', 1e-1, ['dl', 'deciliter', 'deciliters', 'decilitre', 'decilitres']),
  u('liter', 'Liter', 'l', 'volume', 1, ['l', 'liter', 'liters', 'litre', 'litres']),
  u('metricTeaspoon', 'Metric Teaspoon', 'metric tsp', 'volume', 0.005, ['metric tsp', 'metric teaspoon', 'metric teaspoons']),
  u('metricTablespoon', 'Metric Tablespoon', 'metric tbsp', 'volume', 0.015, ['metric tbsp', 'metric tablespoon', 'metric tablespoons']),
  u('ounce', 'Ounce', 'oz', 'mass', 0.028349523125, ['oz', 'ounce', 'ounces']),
  u('pound', 'Pound', 'lb', 'mass', 0.45359237, ['lb', 'lbs', 'pound', 'pounds']),
  u('usFluidOunce', 'US Fluid Ounce', 'US fl oz', 'volume', 0.0295735295625, ['us fl oz', 'us fluid ounce', 'us fluid ounces', 'fluid ounce', 'fluid ounces', 'fl oz']),
  u('usCup', 'US Cup', 'US cup', 'volume', 0.2365882365, ['us cup', 'us cups', 'cup', 'cups']),
  u('usPint', 'US Pint', 'US pt', 'volume', 0.473176473, ['us pt', 'us pint', 'us pints', 'pint', 'pints']),
  u('usQuart', 'US Quart', 'US qt', 'volume', 0.946352946, ['us qt', 'us quart', 'us quarts', 'quart', 'quarts']),
  u('usGallon', 'US Gallon', 'US gal', 'volume', 3.785411784, ['us gal', 'us gallon', 'us gallons', 'gallon', 'gallons']),
  u('usTeaspoon', 'US Teaspoon', 'US tsp', 'volume', 0.00492892159375, ['us tsp', 'us teaspoon', 'us teaspoons', 'tsp', 'teaspoon', 'teaspoons']),
  u('usTablespoon', 'US Tablespoon', 'US tbsp', 'volume', 0.01478676478125, ['us tbsp', 'us tbs', 'us tablespoon', 'us tablespoons', 'tbsp', 'tbs', 'tablespoon', 'tablespoons']),
  u('imperialFluidOunce', 'Imperial Fluid Ounce', 'imp fl oz', 'volume', 0.0284130625, ['imp fl oz', 'imperial fluid ounce', 'imperial fluid ounces']),
  u('imperialCup', 'Imperial Cup', 'imp cup', 'volume', 0.284130625, ['imp cup', 'imperial cup', 'imperial cups']),
  u('imperialPint', 'Imperial Pint', 'imp pt', 'volume', 0.56826125, ['imp pt', 'imperial pint', 'imperial pints']),
  u('imperialQuart', 'Imperial Quart', 'imp qt', 'volume', 1.1365225, ['imp qt', 'imperial quart', 'imperial quarts']),
  u('imperialGallon', 'Imperial Gallon', 'imp gal', 'volume', 4.54609, ['imp gal', 'imperial gallon', 'imperial gallons']),
  u('imperialTeaspoon', 'Imperial Teaspoon', 'imp tsp', 'volume', 0.005919388020833334, ['imp tsp', 'imperial teaspoon', 'imperial teaspoons']),
  u('imperialTablespoon', 'Imperial Tablespoon', 'imp tbsp', 'volume', 0.0177581640625, ['imp tbsp', 'imperial tablespoon', 'imperial tablespoons']),
];

export const SEED_UNIT: Record<SeedUnitKey, SeedUnit> = Object.fromEntries(
  SEED_UNITS.map(x => [x.key, x]),
) as Record<SeedUnitKey, SeedUnit>;

/** Units offered in pickers per system — same lists as Swift's UnitSystem.seedUnits. */
export const SYSTEM_UNITS: Record<UnitSystem, SeedUnitKey[]> = {
  metric: ['liter', 'deciliter', 'centiliter', 'milliliter', 'gram', 'milligram', 'kilogram', 'metricTeaspoon', 'metricTablespoon'],
  usCustomary: ['ounce', 'pound', 'usFluidOunce', 'usCup', 'usPint', 'usQuart', 'usGallon', 'usTeaspoon', 'usTablespoon'],
  britishImperial: ['ounce', 'pound', 'imperialFluidOunce', 'imperialCup', 'imperialPint', 'imperialQuart', 'imperialGallon', 'imperialTeaspoon', 'imperialTablespoon'],
  grug: [],
};

/** A user-defined unit (Swift IngredientUnit). `toBase` null = not convertible ("pinch", "clove"). */
export type CustomUnit = {
  id: string;
  title: string;
  symbol: string;
  searchTerms: string[];
  dimension: Dimension | null;
  toBase: number | null;
};

/** A concrete unit as used in calculations. */
export type ResolvedUnit = { symbol: string; title: string; dimension: Dimension | null; toBase: number | null; seed?: SeedUnitKey };

export type UnitRef = { seed: SeedUnitKey } | { custom: string } | null;

export function resolveUnit(ref: UnitRef, customUnits: Record<string, CustomUnit>): ResolvedUnit | null {
  if (!ref) return null;
  if ('seed' in ref) {
    const s = SEED_UNIT[ref.seed];
    return { symbol: s.symbol, title: s.title, dimension: s.dimension, toBase: s.toBase, seed: s.key };
  }
  const c = customUnits[ref.custom];
  return c ? { symbol: c.symbol, title: c.title, dimension: c.dimension, toBase: c.toBase } : null;
}

/** Converts `value` between two units of the same dimension; null if not convertible. */
export function convert(value: number, from: ResolvedUnit, to: ResolvedUnit): number | null {
  if (!from.dimension || from.dimension !== to.dimension || !from.toBase || !to.toBase) return null;
  return (value * from.toBase) / to.toBase;
}

export function systemOf(key: SeedUnitKey): UnitSystem[] {
  return (Object.keys(SYSTEM_UNITS) as UnitSystem[]).filter(sys => SYSTEM_UNITS[sys].includes(key));
}

// Readable unit ladders per system & dimension, smallest first. Each entry: unit + the
// smallest value (in that unit) at which it is preferred over the previous step.
type Ladder = { unit: SeedUnitKey; minInUnit: number }[];
const LADDERS: Record<Exclude<UnitSystem, 'grug'>, Record<Dimension, Ladder>> = {
  metric: {
    mass: [{ unit: 'milligram', minInUnit: 0 }, { unit: 'gram', minInUnit: 1 }, { unit: 'kilogram', minInUnit: 1 }],
    volume: [{ unit: 'milliliter', minInUnit: 0 }, { unit: 'liter', minInUnit: 1 }],
  },
  usCustomary: {
    mass: [{ unit: 'ounce', minInUnit: 0 }, { unit: 'pound', minInUnit: 1 }],
    volume: [{ unit: 'usTeaspoon', minInUnit: 0 }, { unit: 'usTablespoon', minInUnit: 1 }, { unit: 'usCup', minInUnit: 0.25 }],
  },
  britishImperial: {
    mass: [{ unit: 'ounce', minInUnit: 0 }, { unit: 'pound', minInUnit: 1 }],
    volume: [
      { unit: 'imperialTeaspoon', minInUnit: 0 },
      { unit: 'imperialTablespoon', minInUnit: 1 },
      { unit: 'imperialFluidOunce', minInUnit: 2 },
      { unit: 'imperialPint', minInUnit: 0.5 },
    ],
  },
};

/**
 * "Convert to my units": returns the amount in the most readable unit of `system`.
 * Units that already belong to `system` are left exactly as entered. Units with no
 * dimension (custom "pinch") or the 'grug' system are left unchanged.
 */
export function toSystem(
  value: number,
  unit: ResolvedUnit,
  system: UnitSystem,
): { value: number; unit: ResolvedUnit } {
  if (system === 'grug' || !unit.dimension || !unit.toBase) return { value, unit };
  if (unit.seed && SYSTEM_UNITS[system].includes(unit.seed)) return { value, unit };
  const ladder = LADDERS[system][unit.dimension];
  const base = value * unit.toBase;
  let chosen = ladder[0].unit;
  for (const step of ladder) {
    if (base / SEED_UNIT[step.unit].toBase >= step.minInUnit) chosen = step.unit;
  }
  const s = SEED_UNIT[chosen];
  return {
    value: base / s.toBase,
    unit: { symbol: s.symbol, title: s.title, dimension: s.dimension, toBase: s.toBase, seed: s.key },
  };
}
